<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
    <div class="container mx-auto px-4 py-12">
      <!-- ヘッダー -->
      <header class="text-center mb-12">
        <h1 class="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
          Soarimetric
        </h1>
        <p class="text-xl md:text-2xl text-gray-600 mb-6">
          スカイスポーツ専用気象予報サイト
        </p>
        <div class="max-w-3xl mx-auto mb-8">
          <p class="text-base md:text-lg text-gray-700 leading-relaxed">
            スカイスポーツ愛好家のための気象情報サービス。<br>
            天気予報に加えて、地上から上空まで9段階の高度別風速データ、エマグラムなど、<br class="hidden md:block">
            フライトに必要な情報を7日間先までお届けします。
          </p>
        </div>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link 
            to="/register"
            class="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            無料で登録する
          </router-link>
          <router-link 
            to="/login"
            class="px-8 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-semibold"
          >
            ログイン
          </router-link>
        </div>
      </header>

      <!-- サンプルエリア -->
      <section class="bg-white rounded-lg shadow-md p-8 mb-12">
        <h2 class="text-3xl font-bold text-center mb-8">サンプルエリアで体験</h2>
        <p class="text-center text-gray-600 mb-8">
          登録不要！まずはサンプルエリアで詳細な気象予報をご確認ください
        </p>
        <div v-if="loading" class="flex justify-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
        <div v-else-if="sampleAreas.length > 0" class="grid md:grid-cols-3 gap-6">
          <div 
            v-for="area in sampleAreas" 
            :key="area.id"
            class="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow"
          >
            <h3 class="text-xl font-semibold mb-2">{{ area.area_name }}</h3>
            <p class="text-gray-600 text-sm mb-1">{{ area.area_name_eng }}</p>
            <p class="text-gray-500 text-sm mb-3">{{ area.region }}</p>
            <div class="text-sm text-gray-600 mb-4">
              <p>離陸: {{ area.takeoff_elevation }}m</p>
              <p>着陸: {{ area.landing_elevation }}m</p>
            </div>
            <router-link 
              :to="`/sample/${area.id}`"
              class="block w-full text-center px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
            >
              詳細予報を見る
            </router-link>
          </div>
        </div>
        <div v-else class="text-center text-gray-500">
          サンプルエリアが設定されていません
        </div>
        <div class="mt-8 text-center">
          <p class="text-gray-600 mb-4">
            全エリアの詳細予報を見るには無料登録が必要です
          </p>
          <router-link 
            to="/register"
            class="inline-block px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
          >
            無料で登録する
          </router-link>
        </div>
      </section>

      <!-- 特徴 -->
      <section class="grid md:grid-cols-3 gap-8 mb-12">
        <div class="bg-white p-6 rounded-lg shadow-md">
          <div class="text-blue-600 text-3xl mb-4">🌤️</div>
          <h3 class="text-xl font-semibold mb-2">専門的な気象データ</h3>
          <p class="text-gray-600">
            高度別風速・風向、サーマル予報、雲底高度など、スカイスポーツに必要な専門情報を提供
          </p>
        </div>
        <div class="bg-white p-6 rounded-lg shadow-md">
          <div class="text-blue-600 text-3xl mb-4">📱</div>
          <h3 class="text-xl font-semibold mb-2">モバイル対応</h3>
          <p class="text-gray-600">
            スマートフォンでのアクセスを前提とした設計で、現地からいつでも気象情報を確認可能
          </p>
        </div>
        <div class="bg-white p-6 rounded-lg shadow-md">
          <div class="text-blue-600 text-3xl mb-4">🔔</div>
          <h3 class="text-xl font-semibold mb-2">カスタムアラート</h3>
          <p class="text-gray-600">
あなたの希望する気象条件になったときに通知でお知らせ（将来実装予定）
          </p>
        </div>
      </section>

      <!-- MVP機能 -->
      <section class="bg-white rounded-lg shadow-md p-8">
        <h2 class="text-3xl font-bold text-center mb-8">サイト開発期間中は全機能無料！</h2>
        <div class="max-w-2xl mx-auto">
          <div class="border-2 border-green-500 rounded-lg p-6 text-center">
            <h3 class="text-2xl font-semibold mb-4 text-green-600">全機能無料プラン</h3>
            <p class="text-4xl font-bold mb-6 text-green-600">¥0 <span class="text-base text-gray-500">/無料</span></p>
            <ul class="space-y-3 mb-6 text-left">
              <li class="flex items-center">
                <span class="text-green-500 mr-3 text-xl">✓</span>
                <span class="text-lg">7日間の週間予報</span>
              </li>
              <li class="flex items-center">
                <span class="text-green-500 mr-3 text-xl">✓</span>
                <span class="text-lg">詳細な気象データ可視化</span>
              </li>
              <li class="flex items-center">
                <span class="text-green-500 mr-3 text-xl">✓</span>
                <span class="text-lg">高度別風速・風向予報</span>
              </li>
              <li class="flex items-center">
                <span class="text-green-500 mr-3 text-xl">✓</span>
                <span class="text-lg">マイエリア登録（最大3箇所）</span>
              </li>
            </ul>
            <div class="bg-blue-50 p-4 rounded-lg">
              <p class="text-sm text-blue-800">
                <strong>サイト開発期間中は全機能を無料でご利用いただけます。</strong><br>
                みなさまのフィードバックをお聞かせください。
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- 免責事項 -->
      <footer class="mt-12 text-center text-sm text-gray-500">
        <p class="mb-2">
          本サービスはOpen-Meteo APIから取得した海外の気象データを表示するものです。
        </p>
        <p class="mb-2">
          これは気象予報ではありません。データには誤差が含まれる可能性があります。
        </p>
        <p>
          正式な気象情報は気象庁の発表をご確認ください。
        </p>
      </footer>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_URL || ''

export default {
  name: 'Home',
  setup() {
    const sampleAreas = ref([])
    const loading = ref(false)

    const fetchSampleAreas = async () => {
      try {
        loading.value = true
        const response = await axios.get(`${API_BASE_URL}/api/weather/sample/areas/`)
        sampleAreas.value = response.data
      } catch (error) {
        console.error('Failed to fetch sample areas:', error)
        sampleAreas.value = []
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchSampleAreas()
    })

    return {
      sampleAreas,
      loading
    }
  }
}
</script>
